import { useMemo, useState } from 'react';
import {
  type DragCancelEvent,
  type DragEndEvent,
  type DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';

import type { ContentSection } from './sectionTypes';

interface UseSectionDragAndDropParams {
  disabled?: boolean;
  onReorder(activeSectionId: string, overSectionId: string): void;
  sections: ContentSection[];
}

export function useSectionDragAndDrop({
  disabled = false,
  onReorder,
  sections,
}: UseSectionDragAndDropParams) {
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const sectionIds = useMemo(
    () => sections.map((section) => section.id),
    [sections],
  );
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function handleDragStart(event: DragStartEvent) {
    if (disabled) {
      return;
    }

    setActiveSectionId(String(event.active.id));
  }

  function handleDragCancel(_event: DragCancelEvent) {
    setActiveSectionId(null);
  }

  function handleDragEnd(event: DragEndEvent) {
    setActiveSectionId(null);

    if (disabled) {
      return;
    }

    const activeId = String(event.active.id);
    const overId = event.over ? String(event.over.id) : null;

    if (!overId || activeId === overId) {
      return;
    }

    onReorder(activeId, overId);
  }

  return {
    activeSectionId,
    handleDragCancel,
    handleDragEnd,
    handleDragStart,
    sectionIds,
    sensors,
  };
}
